import React, { useState } from "react"
import { useQuery, useQueryClient } from "react-query"
import { AxiosError } from "axios"
import { ICandidatCommentaireModel, IHttpErrorResponseModel } from "@common-models/*"
import { toast } from "react-toastify"
import { addCommentaire, getCommentaires } from "../../../../core/_requests"
import { useSearchResultsContext } from "../../CandidatsRechercheResults"
import MyAlert from "@common-utils/MyAlert"
import MyTooltip from "@common-utils/MyTooltip"
import FileAndDriveHelper from "@common-helpers/FileAndDriveHelper"
import { toAbsoluteUrl } from "@common-metronic/helpers"
import * as Yup from "yup"
import { Form, Formik, FormikHelpers } from "formik"
import MyEditorField from "@common-utils/fields/MyEditorField"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import { useSearchResultsPreviewContext } from "../_CandidatsRechercheResultsPreview"

const REACT_QUERY_KEY_GET_COMMENTAIRES_QUERY = "REACT_QUERY_KEY_GET_COMMENTAIRES_QUERY"

export const CandidatsRechercheResultsPreviewHelpersComments = () => {
     const queryClient = useQueryClient()
     const globalContext = useSearchResultsContext()
     const previewContext = useSearchResultsPreviewContext()
     const [isAddingCommentaire, setIsAddingCommentaire] = useState<boolean>(false)

     // --- Query Logic ---
     const commentairesQuery = useQuery<ICandidatCommentaireModel[], AxiosError>(
          [REACT_QUERY_KEY_GET_COMMENTAIRES_QUERY, globalContext.itemInPreview?.selectedItem.candidat.id],
          async () => {
               return getCommentaires(globalContext.itemInPreview?.selectedItem.candidat.id as number)
                    .then(r => r.data)
                    .catch((e: AxiosError) => {
                         const error: IHttpErrorResponseModel = e.response?.data
                         toast.error(error?.detail, { autoClose: false })
                         throw e
                    })
                    .finally(() => {
                         previewContext.setIsPreviewBodyLoading(false)
                         globalContext.setItemInPreview(prev => (prev ? { ...prev, isSelectedItemLoading: false } : null))
                    })
          }
     )

     // --- Sub-Components ---

     const LoadingState = () => (
          <div className="py-4">
               {[1, 2].map(i => (
                    <div key={i} className="d-flex mb-5 animate-pulse">
                         <div className="symbol symbol-40px me-4 bg-light rounded-circle h-40px w-40px"></div>
                         <div className="flex-grow-1">
                              <div className="h-10px bg-light rounded w-25 mb-2"></div>
                              <div className="h-50px bg-light rounded w-100"></div>
                         </div>
                    </div>
               ))}
          </div>
     )

     const EmptyState = () => (
          <div className="d-flex flex-column align-items-center justify-content-center py-10 text-muted">
               <div className="symbol symbol-60px mb-3">
                    <span className="symbol-label bg-light-primary">
                         <i className="fas fa-comments fs-2x text-primary" />
                    </span>
               </div>
               <span className="fs-6 fw-bold text-gray-500">Aucun commentaire pour le moment</span>
               <span className="fs-7 text-gray-400">Soyez le premier à en ajouter un.</span>
          </div>
     )

     const CommentaireItem = ({ item }: { item: ICandidatCommentaireModel }) => {
          const avatarUrl = item.personnel?.avatarGoogleDriveId
               ? FileAndDriveHelper.getThumbnailDriveUrl(item.personnel?.avatarGoogleDriveId)
               : toAbsoluteUrl("/media/svg/avatars/blank.svg")

          return (
               <div className="d-flex mb-7">
                    {/* Avatar */}
                    <div className="me-5">
                         <div className="symbol symbol-45px symbol-circle">
                              <MyTooltip title={<>{item.personnel?.prenomNom}</>}>
                                   <img src={avatarUrl} alt="avatar" className="object-fit-cover" />
                              </MyTooltip>
                         </div>
                    </div>

                    {/* Content Bubble */}
                    <div className="flex-grow-1">
                         <div className="bg-light-primary rounded p-4">
                              <div className="d-flex flex-column">
                                   <div className="d-flex align-items-center mb-2">
                                        <span className="text-gray-900 fw-bold fs-6 me-2">{item.personnel?.prenomNom}</span>
                                        <span className="text-muted fw-semibold fs-7">{item.date?.fromNow() || item.date?.format("DD/MM/YYYY")}</span>
                                   </div>
                                   <span className="text-gray-800 fs-6" style={{ wordBreak: "break-word" }} dangerouslySetInnerHTML={{ __html: item.commentaire || "" }} />
                              </div>
                         </div>
                         {/* Timestamp precise tooltip on hover could go here */}
                    </div>
               </div>
          )
     }

     const FormAddCommentaire = () => {
          const schema = Yup.object().shape({
               commentaire: Yup.string().required("Le commentaire ne peut pas être vide"),
          })

          function handleSubmit(values: { commentaire: string }, helpers: FormikHelpers<{ commentaire: string }>) {
               // Cast is no longer strictly necessary if mapped correctly, but safe to keep
               addCommentaire(globalContext.itemInPreview?.selectedItem.candidat.id as number, values.commentaire)
                    .then(r => {
                         queryClient.setQueryData<ICandidatCommentaireModel[] | undefined>(
                              [REACT_QUERY_KEY_GET_COMMENTAIRES_QUERY, globalContext.itemInPreview?.selectedItem.candidat.id],
                              prev => (prev ? [r.data, ...prev] : [r.data])
                         )
                         toast.success("Commentaire ajouté avec succès.")
                         setIsAddingCommentaire(false)
                    })
                    .catch((e: AxiosError) => {
                         const error: IHttpErrorResponseModel = e.response?.data
                         helpers.setStatus(error?.detail)
                         toast.error(error?.detail || "Erreur lors de l'ajout", { autoClose: false })
                         helpers.setSubmitting(false)
                    })
          }
          return (
               <div className="bg-light-primary rounded p-5 border border-primary border-dashed animate__animated animate__fadeIn">
                    <h4 className="fs-6 fw-bold text-gray-800 mb-4">Nouveau commentaire</h4>
                    <Formik initialValues={{ commentaire: "" }} onSubmit={handleSubmit} validationSchema={schema}>
                         {helpers => (
                              <Form noValidate autoComplete="off">
                                   <div className="mb-4">
                                        <MyEditorField
                                             value={helpers.values.commentaire}
                                             height={200}
                                             onChange={val => helpers.setFieldValue("commentaire", val)}
                                             placeholder="Rédigez votre note ou commentaire..."
                                        />
                                        {helpers.errors.commentaire && <div className="text-danger mt-2 fs-7">{helpers.errors.commentaire}</div>}
                                   </div>

                                   <div className="d-flex justify-content-end gap-2">
                                        <button
                                             type="button"
                                             className="btn btn-sm btn-light btn-active-light-primary"
                                             onClick={() => setIsAddingCommentaire(false)}
                                             disabled={helpers.isSubmitting}
                                        >
                                             Annuler
                                        </button>
                                        <button className="btn btn-sm btn-primary d-inline-flex align-items-center" type="submit" disabled={helpers.isSubmitting}>
                                             {!helpers.isSubmitting ? (
                                                  "Publier"
                                             ) : (
                                                  <>
                                                       <MySimpleSpinner size="sm" />
                                                       <span className="ms-2">Envoi...</span>
                                                  </>
                                             )}
                                        </button>
                                   </div>
                              </Form>
                         )}
                    </Formik>
               </div>
          )
     }

     // --- Main Render ---

     return (
          <div className="preview-height">
               <div className="text-center mt-4 mb-5">
                    {!isAddingCommentaire && (
                         <button className="btn btn-sm btn-light-primary fw-bold" type="button" onClick={() => setIsAddingCommentaire(true)}>
                              <i className="fas fa-plus fs-7 me-1" />
                              Ajouter
                         </button>
                    )}
               </div>

               {/* Content Section */}
               <div className={"p-4"}>
                    {/* Error Alert */}
                    {commentairesQuery.error && (
                         <MyAlert type="danger" classNames="mb-5">
                              {commentairesQuery.error.response?.data.detail}
                         </MyAlert>
                    )}

                    {/* Form Mode */}
                    {isAddingCommentaire && <FormAddCommentaire />}

                    {/* List Mode (Visible only if not adding, or move above if you want history visible while typing) */}
                    {!isAddingCommentaire && (
                         <div className="hover-scroll-overlay-y pe-2" style={{ height: "calc(100vh - 250px)", maxHeight: "600px" }}>
                              {commentairesQuery.isLoading ? (
                                   <LoadingState />
                              ) : (
                                   <>
                                        {commentairesQuery.data && commentairesQuery.data.length > 0 ? (
                                             <div className="timeline-container">
                                                  {commentairesQuery.data.map((item, index) => (
                                                       <CommentaireItem key={item.id || index} item={item} />
                                                  ))}
                                             </div>
                                        ) : (
                                             <EmptyState />
                                        )}
                                   </>
                              )}
                         </div>
                    )}
               </div>
          </div>
     )
}
